/**
 * Pure helpers for the ammo-sales trend chart. Keep free of store and DOM access
 * so they can be unit tested directly.
 */

import { isRevenueReason } from './ammoReasons';

export const MONTH_SHORT = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

export const TREND_MONTHS = 12;

/** Window of the per-product stock micro graphs on the ammo-sales stock table. */
export const STOCK_SERIES_DAYS = 90;

function pad(n) {
    return String(n).padStart(2, '0');
}

function isoDate(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The last `months` calendar months ending with the current one, as 'YYYY-MM' keys. */
export function trendMonths(now = new Date(), months = TREND_MONTHS) {
    const out = [];
    for (let i = months - 1; i >= 0; i -= 1) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        out.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
    }
    return out;
}

/** Date filter for the trend window: first day of the earliest month to the last day of this month. */
export function trendRange(now = new Date(), months = TREND_MONTHS) {
    const start = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate: isoDate(start), endDate: isoDate(end) };
}

/** 'YYYY-MM' to a short axis label like 'sep 26'. */
export function monthLabel(key) {
    const [year, month] = String(key).split('-');
    const name = MONTH_SHORT[parseInt(month, 10) - 1] || month;
    return `${name} ${String(year).slice(2)}`;
}

/**
 * Build LineChartCompare input from the stats byMonthCaliber rows: one solid series per
 * caliber for sold units, plus a dashed series for internal use when there is any.
 * Zero-filled for every month in the window so lines stay continuous.
 * @param {Array} rows   [{ month, caliber, unitsSold, roundsSold, unitsInternal, roundsInternal }]
 * @param {object} opts  metric 'units' | 'rounds', now, months, colors, internalLabel
 */
export function buildTrendSeries(rows = [], {
    metric = 'units', now = new Date(), months = TREND_MONTHS, colors = [], internalLabel = 'internt',
} = {}) {
    const keys = trendMonths(now, months);
    const soldField = metric === 'rounds' ? 'roundsSold' : 'unitsSold';
    const internalField = metric === 'rounds' ? 'roundsInternal' : 'unitsInternal';
    const calibers = [...new Set(rows.map(r => r.caliber).filter(Boolean))].sort();
    const pointsFor = (caliber, field) => keys.map((key, idx) => {
        const y = rows
            .filter(r => r.caliber === caliber && r.month === key)
            .reduce((sum, r) => sum + (Number(r[field]) || 0), 0);
        return { x: idx + 1, y };
    });
    const series = [];
    calibers.forEach((caliber, i) => {
        const color = colors.length > 0 ? colors[i % colors.length] : 'currentColor';
        series.push({ label: caliber, color, points: pointsFor(caliber, soldField) });
        // Internal use (sighting-in, courses, competitions) as a dashed line in the same colour.
        const internal = pointsFor(caliber, internalField);
        if (internal.some(p => p.y > 0)) {
            series.push({
                label: `${caliber} ${internalLabel}`, color, dash: true, points: internal,
            });
        }
    });
    return {
        xLabels: keys.map((key, idx) => idx + 1),
        xTickLabels: keys.map(monthLabel),
        series,
    };
}

/** Ammo lines of a sale: those flagged isAmmo, falling back to the first line for old data. */
function ammoLines(sale) {
    const lines = sale?.products || [];
    const ammo = lines.filter(line => line && line.isAmmo);
    if (ammo.length > 0) {
        return ammo;
    }
    return lines[0] ? [lines[0]] : [];
}

/**
 * Trend rows for a member's own purchases, same shape as stats.byMonthCaliber so the
 * list from /api/ammosales/my can feed AmmoTrendChart without another API call.
 * Sales count as sold; every other reason (sighting-in, courses, competitions) counts
 * as internal use, drawn dashed. Month is the UTC month, as in the backend stats.
 */
export function myPurchasesTrendRows(sales = []) {
    const byKey = {};
    sales.forEach(sale => {
        const date = sale.saleDate ? new Date(sale.saleDate) : null;
        if (!date || Number.isNaN(date.getTime())) {
            return;
        }
        const month = date.toISOString().slice(0, 7);
        const revenue = isRevenueReason(sale.ammoReason);
        ammoLines(sale).forEach(line => {
            const caliber = line.caliber || line.name || '';
            if (!caliber) {
                return;
            }
            const key = `${month}|${caliber}`;
            if (!byKey[key]) {
                byKey[key] = {
                    month, caliber, unitsSold: 0, roundsSold: 0, unitsInternal: 0, roundsInternal: 0,
                };
            }
            const qty = Number(line.qty) || 0;
            const rounds = qty * (Number(line.roundsPerUnit) || 0);
            if (revenue) {
                byKey[key].unitsSold += qty;
                byKey[key].roundsSold += rounds;
            } else {
                byKey[key].unitsInternal += qty;
                byKey[key].roundsInternal += rounds;
            }
        });
    });
    return Object.values(byKey).sort((a, b) => a.month.localeCompare(b.month) || a.caliber.localeCompare(b.caliber));
}
